// istanbul ignore file
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { appendToQueryString } from '@cotyorg/ccx-utils/js/helpers/url';
import { getBreakpoints } from '@cotyorg/ccx-utils/js/components/BreakpointProvider/breakpointHelper';
import R from 'ramda';
import { getWindow } from '@cotyorg/ccx-utils/js/helpers/globals';

const LOW_QUALITY_RETRIES_LIMIT = 7;
const HIGH_QUALITY_RETRIES_LIMIT = 7;

const qualityQueryStringParam = 'quality';
const supportsObjectFit =
  typeof document === 'undefined'
    ? true
    : 'objectFit' in document.documentElement.style;

const fetchImage = ({ src, windowReference }) =>
  new Promise((resolve, reject) => {
    const image = new windowReference.Image();
    image.onload = resolve;
    image.onerror = reject;
    image.src = src;
  });

const generatePictureWrapperClassNames = ({ alignment, wrapperExtraClass }) =>
  classNames('flex-container', {
    [`align-${alignment}`]: alignment,
    [wrapperExtraClass]: wrapperExtraClass,
  });

const generatePictureClassNames = ({
  isHighQuality,
  cover,
  extraClass,
  isBaseQualityError,
  showPlaceholder,
}) =>
  classNames('picture', {
    'picture--low-quality': !isHighQuality,
    'picture--download-error': isBaseQualityError,
    [`${extraClass}`]: extraClass,
    'picture--cover': cover,
    'picture--placeholder': showPlaceholder,
  });

const generateImageClassNames = ({ cover }) =>
  classNames('picture__image', {
    'picture__image--cover': cover,
  });

class Picture extends Component {
  constructor(props) {
    super(props);

    this.id = props.src;
    this.fetchLowQualityTimeout = null;
    this.fetchHighQualityTimeout = null;

    // This is needed because matchMedia and image preloading does not work correctly inside an iframe otherwise
    this.windowReference = typeof window !== 'undefined' && getWindow();

    if (!this.props.picture) {
      // Prevent multiple instances of the same image src being re-fetched by keeping redux state the same
      this.props.initialisePicture(this.id);
    }

    if (this.windowReference.navigator) {
      this.props.setOnlineStatus(
        this.id,
        this.windowReference.navigator.onLine,
      );
    }
  }

  componentDidMount() {
    this.windowReference.addEventListener(
      'online',
      this.handleConnectionChange,
    );
    this.windowReference.addEventListener(
      'offline',
      this.handleConnectionChange,
    );
  }

  componentDidUpdate(prevProps) {
    if (!this.props.src && !this.props.srcset) {
      return;
    }

    if (
      prevProps.currentBreakpoint !== this.props.currentBreakpoint &&
      !this.isLowQualityLoaded()
    ) {
      // stop fetching the low quality image for the previous breakpoint
      clearTimeout(this.fetchLowQualityTimeout);
      clearTimeout(this.fetchHighQualityTimeout);
      // allow low quality refetch to restart for the next breakpoint
      this.props.resetLowQualityRetries(this.id);
      this.props.resetHighQualityRetries(this.id);
    }

    const breakpointArray = Array.isArray(this.props.srcset)
      ? getBreakpoints(
        this.props.breakpoints,
        this.props.srcset.map(({ name }) => name),
      )
      : [];

    const name = R.compose(
      R.propOr('', 'name'),
      R.defaultTo({}),
      R.find(({ mediaQueryString }) =>
        this.windowReference.matchMedia(mediaQueryString).matches),
    )(breakpointArray);

    const breakpointSrc = R.compose(
      R.prop('src'),
      R.defaultTo({}),
      R.find(R.propEq('name', name)),
      R.defaultTo({}),
    )(this.props.srcset);

    const src = breakpointSrc || this.props.src;

    const loadLowQuality =
      !this.props.doNotLazyLoad &&
      this.props.isWithinViewport &&
      !this.isLowQualityLoading() &&
      !this.isLowQualityLoaded() &&
      !this.props.disableProgressiveLoading;

    if (loadLowQuality) {
      this.loadLowQuality({ breakpointSrc: src });
    }

    // Only load high quality image now if still in viewport and low quality image has already loaded
    // This ensures images are loaded progressively, and that when the user scrolls down the page high
    // resolution images aren't loaded for offscreen images (until later scrolled back into view)
    const loadHighQuality =
      !this.props.doNotLazyLoad &&
      this.props.isWithinViewport &&
      (this.isLowQualityLoaded() || !!this.props.disableProgressiveLoading) &&
      !this.isHighQualityLoading() &&
      !this.isHighQualityLoaded();

    if (loadHighQuality) {
      this.loadHighQuality({ breakpointSrc: src });
    }
  }

  componentWillUnmount() {
    this.windowReference.removeEventListener(
      'online',
      this.handleConnectionChange,
    );
    this.windowReference.removeEventListener(
      'offline',
      this.handleConnectionChange,
    );
    clearTimeout(this.fetchLowQualityTimeout);
    clearTimeout(this.fetchHighQualityTimeout);
  }

  getStateProperty(propName) {
    return this.props.picture && this.props.picture[propName];
  }

  handleConnectionChange = () => {
    if (!this.isHighQualityLoaded()) {
      this.props.setOnlineStatus(
        this.id,
        this.windowReference.navigator.onLine,
      );
    }
  };

  // Preload either the low quality img or current breakpoint picture src depending on browser support
  loadLowQuality({ breakpointSrc }) {
    if (
      this.getStateProperty('stopLowQualityRetries') ||
      this.isLowQualityLoading() ||
      this.isLowQualityLoaded() ||
      !this.windowReference ||
      !this.getStateProperty('online')
    ) {
      return;
    }

    this.props.setLowQualityLoading(this.id);

    // eslint-disable-next-line
    const preloadSrc = !!window.HTMLPictureElement
      ? breakpointSrc
      : this.props.src;

    // TODO: make these requests from redux so they can be throttled, cancelled, etc
    // This could enable cancelling of requests when going out of viewport (especially for high quality)
    const getLowQualityTimeout = () =>
      this.getStateProperty('timeouts')[
        this.getStateProperty('lowQualityRetries')
      ];

    this.fetchLowQualityTimeout = setTimeout(() => {
      fetchImage({
        src: appendToQueryString(preloadSrc, {
          [qualityQueryStringParam]: '10',
        }),
        windowReference: this.windowReference,
      })
        .then(() => {
          this.props.setLowQualityLoaded(this.id);
        })
        // catch in case the image download fails
        .catch(() => {
          this.props.setLowQualityLoading(this.id, false);
          this.props.setBaseQualityError();
          this.props.setLowQualityRetries(
            this.id,
            this.getStateProperty('lowQualityRetries') + 1,
          );

          if (
            this.getStateProperty('lowQualityRetries') >=
            LOW_QUALITY_RETRIES_LIMIT
          ) {
            this.props.setStopLowQualityRetries(this.id, true);
          }
        });
    }, getLowQualityTimeout());
  }

  // Preload either the high quality img or current breakpoint picture src depending on browser support
  loadHighQuality({ breakpointSrc }) {
    if (
      this.getStateProperty('stopHighQualityRetries') ||
      this.isHighQualityLoading() ||
      this.isHighQualityLoaded() ||
      !this.windowReference ||
      !this.getStateProperty('online')
    ) {
      return;
    }

    this.props.setHighQualityLoading(this.id);

    // eslint-disable-next-line
    const preloadSrc = !!window.HTMLPictureElement
      ? breakpointSrc
      : this.props.src;

    // TODO: make these requests from redux so they can be throttled, cancelled, etc
    const getHighQualityTimeout = () =>
      this.getStateProperty('timeouts')[
        this.getStateProperty('highQualityRetries')
      ];

    this.fetchHighQualityTimeout = setTimeout(() => {
      fetchImage({
        src: preloadSrc,
        windowReference: this.windowReference,
      })
        .then(() => {
          this.props.setHighQualityLoaded(this.id);
        })
        // catch in case the image download fails
        .catch(() => {
          this.props.setHighQualityLoading(this.id, false);
          this.props.setHighQualityRetries(
            this.id,
            this.getStateProperty('highQualityRetries') + 1,
          );

          if (
            this.getStateProperty('highQualityRetries') >=
            HIGH_QUALITY_RETRIES_LIMIT
          ) {
            this.props.setStopHighQualityRetries(this.id, true);
          }
        });
    }, getHighQualityTimeout());
  }

  isLowQualityLoaded() {
    return this.getStateProperty('lowQualityLoaded');
  }

  isLowQualityLoading() {
    return this.getStateProperty('lowQualityLoading');
  }

  isBaseQualityError() {
    return this.getStateProperty('baseQualityError');
  }

  isHighQualityLoaded() {
    return this.getStateProperty('highQualityLoaded');
  }

  isHighQualityLoading() {
    return this.getStateProperty('highQualityLoading');
  }

  render() {
    const {
      src,
      srcset,
      alt,
      alignment,
      extraClass,
      doNotLazyLoad,
      imageLoading = 'lazy',
      cover = false,
      breakpoints = {},
      wrapperExtraClass,
      currentBreakpoint,
    } = this.props;

    if (!src && !srcset) {
      return null;
    }

    // This approach means that all JS has to be downloaded and then if the below
    // condition is true a re-render will be triggered before the image is downloaded.
    // There may be a better solution involving; generate whole html on server, and
    // applying default css styles for IE and change them for modern browsers.
    if (cover && !supportsObjectFit) {
      return (
        <div
          className="picture__cover-fallback"
          style={{ backgroundImage: `url(${src})` }}
        />
      );
    }

    const showPlaceholder =
    !doNotLazyLoad &&
    !this.isLowQualityLoaded() &&
    !this.isHighQualityLoaded(); // need to check this too incase progressive image loading is disabled

    if (showPlaceholder) {
      return (
        <div
          className={generatePictureWrapperClassNames({
          alignment,
          wrapperExtraClass,
        })}
        >
          <div className="picture--placeholder" />
        </div>
      );
    }

    // Either low or high quality has been loaded at this point (or we are not lazy loading)
    // Render the appropriate one

    const isHighQuality = this.isHighQualityLoaded() || doNotLazyLoad;
    const queryStringParams = isHighQuality
      ? {}
      : { [qualityQueryStringParam]: '10' };

    const breakpointArray = Array.isArray(srcset)
      ? getBreakpoints(breakpoints, srcset.map(({ name }) => name))
      : [];

    const set = breakpointArray.map(({ name, mediaQueryString }) => {
      const { src: source } = srcset.find(image => image.name === name) || {};

      return (
        <source
          key={name}
          srcSet={appendToQueryString(source, queryStringParams)}
          media={mediaQueryString}
        />
      );
    });

    const { height, width } = R.or(
      R.prop('dimension', R.find(R.propEq('name', currentBreakpoint), R.or(srcset, []))),
      R.propOr({}, 'xsDimension', R.find(R.propEq('name', 'small'), R.or(srcset, []))),
    );

    return (
      <div
        className={generatePictureWrapperClassNames({
          alignment,
          wrapperExtraClass,
        })}
      >
        <picture
          className={generatePictureClassNames({
            isHighQuality,
            extraClass,
            isBaseQualityError: this.isBaseQualityError(),
            cover,
          })}
        >
          {set}

          <img
            className={generateImageClassNames({ cover })}
            ref={(el) => {
              this.imageElement = el;
            }}
            src={appendToQueryString(src, queryStringParams)}
            alt={alt || ''}
            height={height}
            width={width}
            loading={imageLoading}
          />
        </picture>
      </div>
    );
  }
}

Picture.defaultProps = {
  alignment: 'center',
};

Picture.propTypes = {
  src: PropTypes.string.isRequired,
  srcset: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  })),
  alt: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  extraClass: PropTypes.string,
  isWithinViewport: PropTypes.bool,
  doNotLazyLoad: PropTypes.bool,
  imageLoading: PropTypes.oneOf(['lazy', 'eager']),
  initialisePicture: PropTypes.func.isRequired,
  setHighQualityLoading: PropTypes.func.isRequired,
  setHighQualityLoaded: PropTypes.func.isRequired,
  setLowQualityLoading: PropTypes.func.isRequired,
  setLowQualityLoaded: PropTypes.func.isRequired,
  setBaseQualityError: PropTypes.func.isRequired,
  picture: PropTypes.objectOf(PropTypes.bool),
  cover: PropTypes.bool,
  breakpoints: PropTypes.objectOf(PropTypes.string).isRequired,
  wrapperExtraClass: PropTypes.string,
  disableProgressiveLoading: PropTypes.bool,
  setLowQualityRetries: PropTypes.func.isRequired,
  setStopLowQualityRetries: PropTypes.func.isRequired,
  setHighQualityRetries: PropTypes.func.isRequired,
  setStopHighQualityRetries: PropTypes.func.isRequired,
  setOnlineStatus: PropTypes.func.isRequired,
  currentBreakpoint: PropTypes.string,
  resetLowQualityRetries: PropTypes.func,
  resetHighQualityRetries: PropTypes.func,
};

export default Picture;
